// Per-MFE stylesheet isolation.
//
// Every remote is built with `vite-plugin-css-injected-by-js`, so its whole
// stylesheet is appended to `document.head` when the bundle is evaluated and
// stays there for the rest of the session — single-spa unmount only tears down
// the DOM, never the CSS. That is harmless while all remotes compile the same
// Tailwind 4 output (everything lands inside `@layer utilities` / `@layer base`,
// so nothing outranks anything), but `akva-network-scanner` is built with
// Tailwind 3, whose output is UNLAYERED. Unlayered rules beat every layered
// rule regardless of document order, so once the scanner sheet is in the head
// its Preflight (`*{border-width:0}`, `button,input,select{padding:0;margin:0}`,
// `h1..h6{font-size:inherit}`) and its hardcoded-colour utilities override
// tide's Tailwind 4 utilities on every page until a full reload.
//
// Containment: remember which style elements a remote added and switch them off
// with `media="not all"` while it is not mounted.
//
// Ownership is decided by WHO injected the sheet, never by WHEN it appeared.
// Time-based attribution (a head scan, or an observer bounded to the remote's
// load/mount window) cannot work here, because the shell injects stylesheets
// into the same head at the same time: coralreef's emotion runtime emits a
// `<style>` per generated class AS ITS COMPONENTS RENDER, and font-awesome
// injects on demand. Reload straight onto an MFE route and the shell's first
// render — emotion, font-awesome — happens while that MFE's bundle is loading,
// so any window-based scheme adopts the SHELL's stylesheets and switches them
// off the moment the MFE unmounts. That is DEV-2933: no icons, unstyled
// coralreef components, mangled layout on every page after leaving the MFE.
//
// So: `document.head`'s insertion methods are patched, and a `<style>`/`<link>`
// is claimed only when the injecting call stack contains the remote's own URL
// prefix. The remote's bundle (and every chunk it lazy-loads) is served from
// that prefix; the shell's emotion/font-awesome injections come from shell
// chunks and can never match. A sheet nobody claims is left alone — an
// unclaimed sheet leaks, a wrongly-claimed one breaks the page it belongs to.

type Lifecycle = (props: unknown) => Promise<unknown>;
type LifecycleEntry = Lifecycle | Lifecycle[];

type SheetNode = HTMLStyleElement | HTMLLinkElement;

export interface MfeStyleIsolation {
  enable: () => void;
  disable: () => void;
  tracked: () => SheetNode[];
}

const DISABLED_MEDIA = "not all";

interface Claimer {
  marker: string;
  claim: (el: SheetNode) => void;
}

const claimers = new Set<Claimer>();
let patchedHead: HTMLHeadElement | null = null;

const isSheet = (node: unknown): node is SheetNode => {
  const el = node as Element | null;
  if (!el || el.nodeType !== Node.ELEMENT_NODE) return false;
  if (el.tagName === "STYLE") return true;
  return (
    el.tagName === "LINK" &&
    (el.getAttribute("rel") || "").includes("stylesheet")
  );
};

// Only ever builds an Error for a stylesheet insertion, which is rare — the tag
// check in front of it is what keeps this off the hot DOM path.
const attribute = (node: unknown) => {
  if (!claimers.size || !isSheet(node)) return;
  const stack = new Error().stack || "";
  for (const claimer of claimers) {
    if (stack.includes(claimer.marker)) {
      claimer.claim(node);
      return;
    }
  }
};

// Patched on `document.head` itself rather than `Node.prototype`, so the check
// never runs for the app's ordinary DOM churn. Every CSS injector in play
// (css-injected-by-js, vite's dev client, emotion) targets `document.head`.
function patchHead(doc: Document) {
  const head = doc.head;
  if (patchedHead === head) return;
  patchedHead = head;

  const appendChild = head.appendChild;
  head.appendChild = function <T extends Node>(node: T): T {
    attribute(node);
    return appendChild.call(this, node) as T;
  };

  const insertBefore = head.insertBefore;
  head.insertBefore = function <T extends Node>(node: T, ref: Node | null): T {
    attribute(node);
    return insertBefore.call(this, node, ref) as T;
  };

  for (const name of ["append", "prepend"] as const) {
    const native = head[name];
    head[name] = function (...nodes: (Node | string)[]) {
      for (const node of nodes) attribute(node);
      return native.apply(this, nodes);
    };
  }
}

// `remoteUrl` is the module URL single-spa imports (e.g.
// `/akva-analytics/mfe-entry.js`); everything the bundle pulls in sits under the
// same prefix, so the directory is the marker.
const markerFor = (remoteUrl: string) =>
  remoteUrl.replace(/[^/]*$/, "") || remoteUrl;

export function createMfeStyleIsolation(
  doc: Document,
  remoteUrl: string,
): MfeStyleIsolation {
  const tracked = new Map<SheetNode, string>();
  let mounted = false;

  const forget = () => {
    for (const el of tracked.keys()) if (!el.isConnected) tracked.delete(el);
  };

  claimers.add({
    marker: markerFor(remoteUrl),
    claim(el) {
      if (tracked.has(el)) return;
      tracked.set(el, el.media);
      // The bundle injects while it evaluates, which is before mount, and a
      // lazy chunk can still land after unmount. Either way it stays off until
      // this app is actually mounted, so it never styles another remote's page.
      if (!mounted) el.media = DISABLED_MEDIA;
    },
  });
  patchHead(doc);

  return {
    enable() {
      mounted = true;
      forget();
      for (const [el, media] of tracked) el.media = media;
    },
    disable() {
      mounted = false;
      forget();
      for (const el of tracked.keys()) el.media = DISABLED_MEDIA;
    },
    tracked() {
      forget();
      return Array.from(tracked.keys());
    },
  };
}

async function runLifecycle(
  entry: LifecycleEntry | undefined,
  props: unknown,
): Promise<unknown> {
  if (!entry) return undefined;
  if (!Array.isArray(entry)) return entry(props);
  let last: unknown;
  for (const fn of entry) last = await fn(props);
  return last;
}

// Wraps a single-spa app loader so the styles the bundle injects are only active
// while the app is mounted.
export function withStyleIsolation(
  loader: () => Promise<Record<string, unknown>>,
  isolation: MfeStyleIsolation,
) {
  let warned = false;
  const warnIfUnclaimed = () => {
    if (warned || isolation.tracked().length) return;
    warned = true;
    // Nothing matched the url marker, so this remote's CSS is uncontained —
    // either the marker is wrong or the bundle injects from somewhere else.
    console.warn("[shell] style isolation claimed no stylesheets for a remote");
  };

  return async () => {
    const mod = await loader();
    const lifecycle = (key: string) => mod[key] as LifecycleEntry | undefined;
    return {
      bootstrap: async (props: unknown) =>
        runLifecycle(lifecycle("bootstrap"), props),
      mount: async (props: unknown) => {
        warnIfUnclaimed();
        isolation.enable();
        try {
          return await runLifecycle(lifecycle("mount"), props);
        } catch (err) {
          // single-spa marks the app broken and never calls unmount.
          isolation.disable();
          throw err;
        }
      },
      unmount: async (props: unknown) => {
        try {
          return await runLifecycle(lifecycle("unmount"), props);
        } finally {
          isolation.disable();
        }
      },
      update: async (props: unknown) =>
        runLifecycle(lifecycle("update"), props),
    };
  };
}
